import { inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from '../auth/auth.service';

export interface WebSocketMessage {
  type: string;
  freightOrderIds?: string[];
  action?: string;
  timestamp?: string;
}

@Injectable({
  providedIn: 'root',
})
export class WebSocketService implements OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly ngZone = inject(NgZone);

  private socket: WebSocket | null = null;
  private reconnectAttempts = 0;
  private readonly maxReconnectDelay = 30000; // 30s max
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private keepaliveTimer: ReturnType<typeof setInterval> | null = null;
  private visibilityTimer: ReturnType<typeof setTimeout> | null = null;
  private isConnecting = false;
  private manualDisconnect = false;

  private readonly messages$ = new Subject<WebSocketMessage>();
  private readonly connectionState$ = new Subject<boolean>();

  readonly messages = this.messages$.asObservable();
  readonly connected = this.connectionState$.asObservable();

  private sanitizeForLog(value: unknown): string {
    return String(value).replace(/[\r\n]/g, '');
  }

  constructor() {
    this.setupVisibilityListener();
  }

  async connect(): Promise<void> {
    if (!environment.wsUrl) {
      return;
    }
    if (this.isConnecting) {
      console.log('[WS] Already connecting, skipping');
      return;
    }
    if (this.socket?.readyState === WebSocket.OPEN || this.socket?.readyState === WebSocket.CONNECTING) {
      console.log(`[WS] Socket already in state ${this.socket.readyState}, skipping`);
      return;
    }

    this.isConnecting = true;
    this.manualDisconnect = false;

    try {
      const token = await this.authService.getIdToken();
      if (!token) {
        console.error('[WS] No auth token available');
        this.isConnecting = false;
        this.scheduleReconnect();
        return;
      }

      const url = `${environment.wsUrl}?token=${encodeURIComponent(token)}`;
      this.socket = new WebSocket(url);

      this.socket.onopen = () => {
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.connectionState$.next(true);
        this.startKeepalive();
        console.log('[WS] Connected');
      };

      this.socket.onmessage = (event: MessageEvent) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          const sanitizedMessage = this.sanitizeForLog(JSON.stringify(message));
          console.log('[WS] Message received:', this.sanitizeForLog(message.type), sanitizedMessage);
          this.ngZone.run(() => this.messages$.next(message));
        } catch {
          // Ignore non-JSON messages (e.g. pong responses)
        }
      };

      this.socket.onclose = (event: CloseEvent) => {
        this.isConnecting = false;
        this.connectionState$.next(false);
        this.stopKeepalive();
        console.log(`[WS] Disconnected (code: ${event.code})`);

        if (!this.manualDisconnect) {
          this.scheduleReconnect();
        }
      };

      this.socket.onerror = () => {
        this.isConnecting = false;
        // onclose will fire after onerror, which handles reconnection
      };
    } catch (error) {
      console.error('[WS] Connection failed:', error);
      this.isConnecting = false;
      this.scheduleReconnect();
    }
  }

  disconnect(): void {
    this.manualDisconnect = true;
    this.clearTimers();

    if (this.socket) {
      this.socket.close(1000, 'Client disconnect');
      this.socket = null;
    }
  }

  private scheduleReconnect(): void {
    if (this.manualDisconnect || this.reconnectTimer) return;

    // Exponential backoff: 1s, 2s, 4s, 8s, 16s, 30s max
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), this.maxReconnectDelay);
    this.reconnectAttempts++;

    console.log(`[WS] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private startKeepalive(): void {
    this.stopKeepalive();
    // Ping every 5 minutes (API Gateway has 10-min idle timeout)
    this.keepaliveTimer = setInterval(() => {
      if (this.socket?.readyState === WebSocket.OPEN) {
        this.socket.send(JSON.stringify({ action: 'ping' }));
      }
    }, 5 * 60 * 1000);
  }

  private stopKeepalive(): void {
    if (this.keepaliveTimer) {
      clearInterval(this.keepaliveTimer);
      this.keepaliveTimer = null;
    }
  }

  private setupVisibilityListener(): void {
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        // Disconnect after 5 minutes of being hidden
        this.visibilityTimer = setTimeout(() => {
          if (document.hidden) {
            console.log('[WS] Tab hidden for 5 min — disconnecting');
            this.disconnect();
          }
        }, 5 * 60 * 1000);
      } else {
        // Tab became visible — cancel pending disconnect and reconnect
        if (this.visibilityTimer) {
          clearTimeout(this.visibilityTimer);
          this.visibilityTimer = null;
        }
        if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
          this.manualDisconnect = false;
          this.reconnectAttempts = 0;
          this.connect();
        }
      }
    });
  }

  private clearTimers(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.visibilityTimer) {
      clearTimeout(this.visibilityTimer);
      this.visibilityTimer = null;
    }
    this.stopKeepalive();
  }

  ngOnDestroy(): void {
    this.disconnect();
    this.messages$.complete();
    this.connectionState$.complete();
  }
}
